/**
 * VehicleStatusWidget
 * Summerar fordon per valbar status (vehicle_usage_statuses).
 * NULL i databasen visas som "Ingen status".
 */

import React from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { ChartPieIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface VehicleStatusRow {
  statusName: string | null;
  count: number;
}

interface VehicleStatusMeta {
  totalCount: number;
  withStatusCount: number;
  noStatusCount: number;
}

const STATUS_COLORS = [
  { dot: 'bg-amber-500', bar: 'bg-amber-500' },
  { dot: 'bg-orange-500', bar: 'bg-orange-500' },
  { dot: 'bg-rose-500', bar: 'bg-rose-500' },
  { dot: 'bg-blue-500', bar: 'bg-blue-500' },
  { dot: 'bg-purple-500', bar: 'bg-purple-500' },
  { dot: 'bg-teal-500', bar: 'bg-teal-500' },
];
const NO_STATUS_COLOR = { dot: 'bg-gray-400', bar: 'bg-gray-400' };

const VehicleStatusWidget: React.FC = () => {
  const { t } = useTranslation();

  const { data: responseData, isLoading: loading, error: queryError, refetch } = useQuery(
    ['dashboard-widget', '/api/dashboard/data/vehicle-status-summary'],
    async () => {
      const response = await api.get('/api/dashboard/data/vehicle-status-summary');
      if (response.data.success) {
        return {
          data: (response.data.data || []) as VehicleStatusRow[],
          meta: (response.data.meta || null) as VehicleStatusMeta | null,
        };
      }
      throw new Error(response.data.error || 'Failed to load data');
    },
    { staleTime: 60 * 1000 }
  );

  const rows = responseData?.data ?? [];
  const meta = responseData?.meta ?? null;
  const total = meta?.totalCount ?? rows.reduce((sum, r) => sum + r.count, 0);
  const error = queryError ? (queryError as Error).message : null;

  let colorIdx = 0;
  const colorFor = (statusName: string | null) => {
    if (statusName === null) return NO_STATUS_COLOR;
    const color = STATUS_COLORS[colorIdx % STATUS_COLORS.length];
    colorIdx += 1;
    return color;
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error && rows.length === 0) {
    return <div className="text-red-500 text-sm py-2">{error}</div>;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChartPieIcon className="w-8 h-8 text-blue-500" />
          <div>
            <div className="text-2xl font-bold text-textDefault">{total}</div>
            <div className="text-xs text-textSecondary">{t('widgets.vehicleStatusSubtitle')}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="p-1 hover:bg-bgElevated rounded"
          title={t('common.refresh')}
        >
          <ArrowPathIcon className={`w-4 h-4 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="mt-3 pt-3 border-t border-borderDefault space-y-2">
        {rows.length === 0 ? (
          <div className="text-center text-textTertiary text-sm py-2">
            {t('widgets.noVehicles')}
          </div>
        ) : (
          rows.map((row) => {
            const color = colorFor(row.statusName);
            const label = row.statusName ?? t('widgets.noUsageStatus');
            const percent = total > 0 ? Math.round((row.count / total) * 100) : 0;
            return (
              <div key={row.statusName ?? '__none__'} className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${color.dot}`} />
                    <span className="text-textDefault truncate">{label}</span>
                  </span>
                  <span className="text-textSecondary shrink-0 ml-2">
                    {row.count}
                    <span className="text-textTertiary"> ({percent}%)</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-bgElevated overflow-hidden">
                  <div className={`h-full rounded-full ${color.bar}`} style={{ width: `${percent}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VehicleStatusWidget;
